import { motion } from 'framer-motion';
import { FaLinkedin, FaGithub, FaInstagram, FaEnvelope, FaChevronDown, FaMapMarkerAlt } from 'react-icons/fa';
import Button from '../ui/Button';
import avatarImage from '../../assets/images/ionut-avatar-dog.webp';

const Hero = () => {
    const socialLinks = [
        {
            icon: FaLinkedin,
            href: 'https://www.linkedin.com/in/ionut-horia-cheovari-578064291/',
            label: 'LinkedIn'
        },
        {
            icon: FaGithub,
            href: 'https://github.com/CheovariIonutHRS',
            label: 'GitHub'
        },
        {
            icon: FaInstagram,
            href: 'https://www.instagram.com/cheovariionut',
            label: 'Instagram'
        },
        {
            icon: FaEnvelope,
            href: 'mailto:cheovariionut@yahoo.com',
            label: 'Email'
        },
    ];

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] },
        },
    };

    const scrollToAbout = () => {
        document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
            {/* Background gradient */}
            <div className="hero-gradient-bg" />

            <motion.div
                className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center pt-32 md:pt-48 pb-20"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
            >
                {/* Profile Image */}
                <motion.div
                    className="mb-8"
                    variants={itemVariants}
                >
                    <div className="profile-image-glow inline-block">
                        <img
                            src={avatarImage}
                            alt="Cheovari Ionut"
                            width="640"
                            height="640"
                            fetchpriority="high"
                            decoding="async"
                            className="w-40 h-40 md:w-52 md:h-52 rounded-full object-cover"
                        />
                    </div>
                </motion.div>

                {/* Name */}
                <motion.h1
                    className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-heading font-bold mb-4"
                    variants={itemVariants}
                >
                    <span className="text-gradient">Cheovari Ionut</span>
                </motion.h1>

                {/* Titles */}
                <motion.div
                    className="flex flex-wrap justify-center gap-2 md:gap-4 mb-6"
                    variants={itemVariants}
                >
                    <span className="text-lg md:text-xl text-text-secondary font-heading">Web Developer</span>
                    <span className="text-text-muted hidden md:inline">•</span>
                    <span className="text-lg md:text-xl text-text-secondary">
                        <a
                            href="https://www.baroul-cluj.ro/tabloul-avocatilor/avocati-suspendati/?wpv_view_count=9650&wpv_post_search=cheovari+ionut&wpv_filter_submit=Cautare"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-gold hover:text-accent-gold-hover transition-colors font-heading"
                        >
                            Lawyer
                        </a>
                    </span>
                    <span className="text-text-muted hidden md:inline">•</span>
                    <span className="text-lg md:text-xl text-text-secondary">
                        Founder of{' '}
                        <a
                            href="https://destroyedsecure.ro"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-accent-blue hover:text-accent-blue-hover transition-colors"
                        >
                            Destroyed Secure
                        </a>
                    </span>
                </motion.div>

                {/* Location */}
                <motion.div
                    className="flex items-center justify-center gap-2 text-text-muted mb-8"
                    variants={itemVariants}
                >
                    <FaMapMarkerAlt className="text-accent-blue" />
                    <span>Cluj-Napoca, Romania</span>
                </motion.div>

                {/* Social Links */}
                <motion.div
                    className="flex justify-center gap-3 mb-10"
                    variants={itemVariants}
                >
                    {socialLinks.map((social) => (
                        <motion.a
                            key={social.label}
                            href={social.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="social-icon"
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            whileTap={{ scale: 0.95 }}
                            aria-label={social.label}
                        >
                            <social.icon className="text-xl" />
                        </motion.a>
                    ))}
                </motion.div>

                {/* CTA Buttons */}
                <motion.div
                    className="flex flex-col sm:flex-row justify-center gap-4"
                    variants={itemVariants}
                >
                    <Button
                        href="/CV.pdf"
                        download="CV.pdf"
                        variant="primary"
                        size="lg"
                    >
                        Download CV
                    </Button>
                    <Button
                        variant="secondary"
                        size="lg"
                        onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                    >
                        Get in Touch
                    </Button>
                </motion.div>
            </motion.div>

            {/* Scroll indicator */}
            <motion.button
                className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-text-muted hover:text-accent-blue transition-colors"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.2, duration: 0.6 }}
                onClick={scrollToAbout}
                aria-label="Scroll to content"
            >
                <motion.div
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
                >
                    <FaChevronDown className="text-2xl" />
                </motion.div>
            </motion.button>
        </section>
    );
};

export default Hero;
